import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Users, 
  Upload, 
  Settings, 
  LogOut, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  UserCog,
  Home,
  X,
  Save,
  AlertTriangle,
  FileText,
  Globe,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  Copy,
  Server,
  ShieldCheck,
  BarChart3,
  TrendingUp,
  Package,
  Activity
} from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

// ============= GOOGLE SHEETS CONFIG COMPONENT =============
const GoogleSheetsConfigSection = ({ API, axios, toast }) => {
  const [config, setConfig] = useState({ spreadsheet_id: '', sheet_name: 'Leads Demo', service_account_email: '', is_configured: false });
  const [saJson, setSaJson] = useState('');
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/superadmin/google-sheets-config`);
        setConfig(r.data);
      } catch {}
    };
    load();
  }, [API, axios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { spreadsheet_id: config.spreadsheet_id, sheet_name: config.sheet_name };
      if (saJson.trim()) payload.service_account_json = saJson.trim();
      await axios.put(`${API}/superadmin/google-sheets-config`, payload);
      toast.success('✅ Configuración de Google Sheets guardada');
      // Reload to show SA email
      const r = await axios.get(`${API}/superadmin/google-sheets-config`);
      setConfig(r.data);
      setSaJson('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/superadmin/google-sheets-config/test`);
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error de conexión');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-green-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
            </svg>
          </div>
          <h3 className="text-lg font-bold text-[#E9FF60]">Google Sheets — Leads de Demo</h3>
        </div>
        <div className="flex items-center gap-2">
          {config.is_configured && (
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">
              <CheckCircle className="w-3.5 h-3.5" /> CONFIGURADO
            </span>
          )}
          <button onClick={() => setShowHelp(!showHelp)} className="text-xs text-gray-400 hover:text-white">
            {showHelp ? 'Ocultar guía' : 'Ver guía de configuración'}
          </button>
        </div>
      </div>

      {showHelp && (
        <div className="mb-6 p-4 bg-[#0a0a0a] border border-[#333] rounded-lg text-xs text-gray-400 space-y-2">
          <p className="font-bold text-white">Cómo configurar Google Sheets (gratuito):</p>
          <ol className="list-decimal list-inside space-y-1.5">
            <li>Andá a <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-[#E9FF60] underline">Google Cloud Console</a> → Creá un proyecto nuevo</li>
            <li>Activá la <strong className="text-white">Google Sheets API</strong> en "APIs y servicios"</li>
            <li>Creá una <strong className="text-white">Cuenta de servicio</strong> → Descargá el JSON de la clave</li>
            <li>Creá una hoja de Google Sheets y <strong className="text-white">compartíla con el email de la cuenta de servicio</strong> (rol: Editor)</li>
            <li>Copiá el ID de la hoja (la parte de la URL entre <code className="text-green-400">/d/</code> y <code className="text-green-400">/edit</code>)</li>
            <li>Pegá el JSON completo de la cuenta de servicio y el Spreadsheet ID abajo → Guardá → Probá la conexión</li>
          </ol>
          <p className="text-gray-500">Cada vez que alguien complete el formulario de demo, se agregará automáticamente una fila en tu hoja.</p>
        </div>
      )}

      {config.service_account_email && (
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <p className="text-xs text-green-400">
            <strong>Cuenta de servicio activa:</strong> <span className="font-mono">{config.service_account_email}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">Asegurate de haber compartido tu hoja con este email (rol: Editor)</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Spreadsheet ID</label>
          <Input
            value={config.spreadsheet_id}
            onChange={(e) => setConfig({ ...config, spreadsheet_id: e.target.value })}
            placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
            className="bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
            data-testid="sheets-spreadsheet-id"
          />
          <p className="text-xs text-gray-500 mt-1">El ID está en la URL de tu Google Sheet</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-300">Nombre de la Hoja</label>
          <Input
            value={config.sheet_name}
            onChange={(e) => setConfig({ ...config, sheet_name: e.target.value })}
            placeholder="Leads Demo"
            className="bg-[#0a0a0a] border-[#333] text-white"
            data-testid="sheets-sheet-name"
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2 text-gray-300">
          Service Account JSON {config.is_configured && <span className="text-green-400 text-xs ml-1">(ya configurado — pegá uno nuevo solo si querés reemplazarlo)</span>}
        </label>
        <textarea
          value={saJson}
          onChange={(e) => setSaJson(e.target.value)}
          placeholder={'{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key_id": "...",\n  "private_key": "-----BEGIN RSA PRIVATE KEY-----\\n..."\n  "client_email": "tu-cuenta@proyecto.iam.gserviceaccount.com",\n  ...\n}'}
          rows={6}
          className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white font-mono text-xs resize-y"
          data-testid="sheets-sa-json"
        />
      </div>

      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold" data-testid="save-sheets-config">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Guardando...' : 'Guardar Google Sheets'}
        </Button>
        {config.is_configured && (
          <Button onClick={handleTest} disabled={testing} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10" data-testid="test-sheets-connection">
            {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {testing ? 'Probando...' : 'Probar Conexión'}
          </Button>
        )}
      </div>
    </div>
  );
};
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isSuperAdmin, loginSuperAdmin, logout } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [businesses, setBusinesses] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, new: 0 });
  
  // Estados para carga masiva
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parsedProducts, setParsedProducts] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Estados para configuración
  const [showFontModal, setShowFontModal] = useState(false);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [fontFile, setFontFile] = useState(null);
  const [fontName, setFontName] = useState('');
  const [fonts, setFonts] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetForm, setPresetForm] = useState({
    name: '',
    primary_color: '#FF6B35',
    accent_color: '#E9FF60',
    background_color: '#0D0D0D',
    text_color: '#FFFFFF',
    card_color: '#262626'
  });

  // Estados para Editor de Páginas (P1)
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [pricingCurrency, setPricingCurrency] = useState('ARS');

  // Estados para Analytics
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsBusinesses, setAnalyticsBusinesses] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Estados para Dominios (P1)
  const [domains, setDomains] = useState([]);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [domainForm, setDomainForm] = useState({
    custom_domain: '',
    business_id: ''
  });

  // Estados para Dominio Principal
  const [mainDomainConfig, setMainDomainConfig] = useState({ domain: '', status: 'not_set', server_ip: '', last_verified: null });
  const [mainDomainInput, setMainDomainInput] = useState('');
  const [serverIP, setServerIP] = useState('');
  const [isManagedCloud, setIsManagedCloud] = useState(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  
  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  
  // Login
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  
  // Formularios
  const [businessForm, setBusinessForm] = useState({
    slug: '',
    name: '',
    owner_email: '',
    password: '',
    whatsapp: '',
    theme_preset: 'modern',
    primary_color: '#FF6B35',
    accent_color: '#E9FF60',
    background_color: '#0D0D0D',
    text_color: '#FFFFFF',
    card_color: '#262626',
    font_heading: 'Playfair Display',
    font_body: 'Roboto'
  });

  useEffect(() => {
    if (isAuthenticated && isSuperAdmin) {
      loadBusinesses();
      loadFonts();
      loadPresets();
      loadPages();
      loadDomains();
      loadMainDomain();
      loadServerIP();
    }
  }, [isAuthenticated, isSuperAdmin]);

  useEffect(() => {
    if (isAuthenticated && isSuperAdmin && activeSection === 'analytics') {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isAuthenticated, isSuperAdmin]);

  const loadPages = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/pages`);
      setPages(response.data);
    } catch (error) {
      console.error('Error loading pages:', error);
    }
  };

  const loadDomains = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/domains`);
      setDomains(response.data);
    } catch (error) {
      console.error('Error loading domains:', error);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [overviewRes, businessesRes] = await Promise.all([
        axios.get(`${API}/superadmin/analytics/overview`),
        axios.get(`${API}/superadmin/analytics/businesses`)
      ]);
      setAnalyticsData(overviewRes.data);
      setAnalyticsBusinesses(businessesRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Error al cargar analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadMainDomain = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/main-domain`);
      setMainDomainConfig(response.data);
      setMainDomainInput(response.data.domain || '');
    } catch (error) {
      console.error('Error loading main domain:', error);
    }
  };

  const loadServerIP = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/server-ip`);
      setServerIP(response.data.ip || '');
      setIsManagedCloud(response.data.is_managed_cloud || false);
    } catch (error) {
      console.error('Error loading server IP:', error);
    }
  };

  const handleSaveMainDomain = async () => {
    if (!mainDomainInput.trim()) {
      toast.error('Ingresá un dominio');
      return;
    }
    try {
      const response = await axios.put(`${API}/superadmin/main-domain`, { domain: mainDomainInput });
      toast.success('Dominio guardado. Ahora podés verificar la conexión DNS.');
      setServerIP(response.data.server_ip || serverIP);
      await loadMainDomain();
      setVerifyResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar dominio');
    }
  };

  const handleVerifyMainDomain = async () => {
    setIsVerifyingDomain(true);
    setVerifyResult(null);
    try {
      const response = await axios.post(`${API}/superadmin/main-domain/verify`);
      setVerifyResult(response.data);
      await loadMainDomain();
      if (response.data.match) {
        toast.success('¡Dominio activo! Los DNS apuntan correctamente.');
      } else {
        toast.warning('El dominio aún no apunta al servidor. Revisá la configuración DNS.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al verificar dominio');
    } finally {
      setIsVerifyingDomain(false);
    }
  };

  const loadFonts = async () => {
    try {
      const [fontsRes, cssRes] = await Promise.all([
        axios.get(`${API}/fonts`),
        axios.get(`${API}/fonts/stylesheet`)
      ]);
      setFonts(fontsRes.data);
      if (cssRes.data.css) {
        let styleEl = document.getElementById('custom-fonts-style');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'custom-fonts-style';
          document.head.appendChild(styleEl);
        }
        styleEl.textContent = cssRes.data.css;
      }
    } catch (error) {
      console.error('Error loading fonts:', error);
    }
  };

  const loadPresets = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/presets`);
      setPresets(response.data);
    } catch (error) {
      console.error('Error loading presets:', error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/businesses`);
      console.log()
      setBusinesses(response.data);
      setStats({
        total: response.data.length,
        active: response.data.length,
        new: response.data.filter(b => {
          const created = new Date(b.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return created > weekAgo;
        }).length
      });
    } catch (error) {
      toast.error('Error al cargar negocios');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await loginSuperAdmin(loginData.username, loginData.password);
    setLoading(false);

    if (result.success) {
      toast.success('¡Bienvenida!');
    } else {
      toast.error(result.error || 'Credenciales incorrectas');
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/superadmin/business`, businessForm);
      toast.success('✅ Negocio creado exitosamente');
      setShowCreateModal(false);
      resetForm();
      loadBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear negocio');
    }
  };

  const handleEditBusiness = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...businessForm };
      delete updateData.slug; // No actualizar slug
      // Remove empty password (only send if changing)
      if (!updateData.password) delete updateData.password;
      
      await axios.put(`${API}/superadmin/business/${selectedBusiness.id}`, updateData);
      toast.success('✅ Negocio actualizado');
      setShowEditModal(false);
      setSelectedBusiness(null);
      loadBusinesses();
    } catch (error) {
      toast.error('Error al actualizar negocio');
    }
  };

  const handleDeleteBusiness = async () => {
    try {
      await axios.delete(`${API}/superadmin/business/${selectedBusiness.id}`);
      toast.success('✅ Negocio eliminado');
      setShowDeleteModal(false);
      setSelectedBusiness(null);
      loadBusinesses();
    } catch (error) {
      toast.error('Error al eliminar negocio');
    }
  };

  const openEditModal = (business) => {
    setSelectedBusiness(business);
    setBusinessForm({
      slug: business.slug,
      name: business.name,
      owner_email: business.owner_email,
      password: '',
      whatsapp: business.whatsapp,
      theme_preset: business.theme_preset || 'modern',
      primary_color: business.primary_color || '#FF6B35',
      accent_color: business.accent_color || '#E9FF60',
      background_color: business.background_color || '#0D0D0D',
      text_color: business.text_color || '#FFFFFF',
      card_color: business.card_color || '#262626',
      font_heading: business.font_heading || 'Playfair Display',
      font_body: business.font_body || 'Roboto',
      panel_config: business.panel_config || {
        orders: true, customers: true, branding: true,
        categories: true, products: true, hours: true,
        zones: true, payments: true
      }
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (business) => {
    setSelectedBusiness(business);
    setShowDeleteModal(true);
  };

  const resetForm = () => {
    setBusinessForm({
      slug: '',
      name: '',
      owner_email: '',
      password: '',
      whatsapp: '',
      theme_preset: 'modern',
      primary_color: '#FF6B35',
      accent_color: '#E9FF60',
      background_color: '#0D0D0D',
      text_color: '#FFFFFF',
      card_color: '#262626',
      font_heading: 'Playfair Display',
      font_body: 'Roboto'
    });
  };

  // Funciones de carga masiva
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    setUploadedFile(file);
    setIsProcessing(true);
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const text = e.target.result;
      const extension = file.name.split('.').pop().toLowerCase();
      
      try {
        let products = [];
        
        if (extension === 'csv' || extension === 'txt') {
          products = parseCSV(text);
        } else if (extension === 'xlsx' || extension === 'xls') {
          toast.error('Archivos Excel: por favor exporta a CSV primero');
          setIsProcessing(false);
          return;
        }
        
        setParsedProducts(products);
        setIsProcessing(false);
        toast.success(`✅ ${products.length} productos detectados`);
      } catch (error) {
        toast.error('Error al procesar el archivo');
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('Archivo vacío o formato inválido');
    }
    
    // Detectar separador (coma o punto y coma)
    const separator = text.includes(';') ? ';' : ',';
    
    const headers = lines[0].split(separator).map(h => h.trim().toLowerCase());
    const products = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator).map(v => v.trim());
      
      if (values.length >= 3) {
        const product = {
          id: `temp-${i}`,
          category: values[0] || 'Sin categoría',
          name: values[1] || `Producto ${i}`,
          price: parseFloat(values[2]) || 0,
          description: values[3] || '',
          image_url: values[4] || null,
          available: true,
          is_popular: false
        };
        
        products.push(product);
      }
    }
    
    return products;
  };

  const handleImportProducts = async () => {
    if (!selectedBusinessId) {
      toast.error('Selecciona un negocio destino');
      return;
    }

    if (parsedProducts.length === 0) {
      toast.error('No hay productos para importar');
      return;
    }

    try {
      setIsProcessing(true);
      
      // Agrupar por categorías
      const categoriesByName = {};
      parsedProducts.forEach(p => {
        if (!categoriesByName[p.category]) {
          categoriesByName[p.category] = [];
        }
        categoriesByName[p.category].push(p);
      });

      // Crear categorías y productos usando endpoints de SuperAdmin
      let totalCreated = 0;
      
      for (const [categoryName, products] of Object.entries(categoriesByName)) {
        // Crear categoría con endpoint especial de SuperAdmin
        const categoryResponse = await axios.post(`${API}/superadmin/category?business_id=${selectedBusinessId}`, {
          name: categoryName,
          order: Object.keys(categoriesByName).indexOf(categoryName),
          hidden: false
        });
        
        const categoryId = categoryResponse.data.id;
        
        // Crear productos de esta categoría
        for (const product of products) {
          await axios.post(`${API}/superadmin/product?business_id=${selectedBusinessId}`, {
            category_id: categoryId,
            name: product.name,
            description: product.description,
            price: product.price,
            image_url: product.image_url,
            available: product.available,
            is_popular: product.is_popular || false,
            discount_price: product.discount_price || null
          });
          totalCreated++;
        }
      }

      toast.success(`✅ ${totalCreated} productos importados exitosamente`);
      
      // Limpiar estado
      setUploadedFile(null);
      setParsedProducts([]);
      setSelectedBusinessId('');
      setIsProcessing(false);
      
    } catch (error) {
      toast.error('Error al importar productos: ' + (error.response?.data?.detail || error.message));
      setIsProcessing(false);
    }
  };

  const updateParsedProduct = (id, field, value) => {
    setParsedProducts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const removeParsedProduct = (id) => {
    setParsedProducts(prev => prev.filter(p => p.id !== id));
  };

  // Funciones de configuración
  const handleFontUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.otf', '.ttf', '.woff', '.woff2'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      
      if (!validExtensions.includes(fileExtension)) {
        toast.error('Formato no válido. Use: .otf, .ttf, .woff, .woff2');
        return;
      }
      
      setFontFile(file);
      // Extraer nombre sin extensión
      const nameWithoutExt = file.name.replace(/\.(otf|ttf|woff|woff2)$/i, '');
      setFontName(nameWithoutExt);
      toast.success('Fuente cargada: ' + file.name);
    }
  };

  const handleSaveFont = async () => {
    if (!fontFile || !fontName) {
      toast.error('Selecciona un archivo y define un nombre');
      return;
    }
    
    try {
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(new Error('Error al leer el archivo'));
        reader.readAsDataURL(fontFile);
      });

      const fileType = fontFile.name.split('.').pop().toLowerCase();
      
      await axios.post(`${API}/superadmin/fonts`, {
        name: fontName,
        file_data: base64Data,
        file_type: fileType
      });
      
      toast.success(`Fuente "${fontName}" agregada al sistema`);
      setShowFontModal(false);
      setFontFile(null);
      setFontName('');
      loadFonts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar fuente');
    }
  };

  const handleDeleteFont = async (fontId) => {
    try {
      await axios.delete(`${API}/superadmin/fonts/${fontId}`);
      toast.success('Fuente eliminada');
      loadFonts();
    } catch (error) {
      toast.error('Error al eliminar fuente');
    }
  };

  const handleSavePreset = async () => {
    if (!presetForm.name) {
      toast.error('Define un nombre para el preset');
      return;
    }
    
    try {
      await axios.post(`${API}/superadmin/presets`, presetForm);
      toast.success(`✅ Preset "${presetForm.name}" creado exitosamente`);
      setShowPresetModal(false);
      setPresetForm({
        name: '',
        primary_color: '#FF6B35',
        accent_color: '#E9FF60',
        background_color: '#0D0D0D',
        text_color: '#FFFFFF',
        card_color: '#262626'
      });
      loadPresets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear preset');
    }
  };

  const handleDeletePreset = async (presetId) => {
    try {
      await axios.delete(`${API}/superadmin/presets/${presetId}`);
      toast.success('Preset eliminado');
      loadPresets();
    } catch (error) {
      toast.error('Error al eliminar preset');
    }
  };

  // Page Editor Functions
  const handleSelectPage = async (pageId) => {
    try {
      const response = await axios.get(`${API}/superadmin/pages/${pageId}`);
      setSelectedPage(pageId);
      setPageContent(response.data.content);
      if (response.data.content.currency) {
        setPricingCurrency(response.data.content.currency);
      }
    } catch (error) {
      toast.error('Error al cargar página');
    }
  };

  const handleSavePage = async () => {
    if (!selectedPage || !pageContent) return;
    
    try {
      await axios.put(`${API}/superadmin/pages/${selectedPage}`, pageContent);
      toast.success('✅ Página guardada');
      loadPages();
    } catch (error) {
      toast.error('Error al guardar página');
    }
  };

  const updatePageContent = (path, value) => {
    const keys = path.split('.');
    const newContent = { ...pageContent };
    let current = newContent;
    
    for (let i = 0; i < keys.length - 1; i++) {
      if (keys[i].includes('[')) {
        const [key, index] = keys[i].replace(']', '').split('[');
        current = current[key][parseInt(index)];
      } else {
        current = current[keys[i]];
      }
    }
    
    const lastKey = keys[keys.length - 1];
    current[lastKey] = value;
    
    setPageContent(newContent);
  };

  // Domain Functions
  const handleAddDomain = async () => {
    if (!domainForm.custom_domain) {
      toast.error('Ingresá un dominio');
      return;
    }
    if (!domainForm.business_id) {
      toast.error('Seleccioná un negocio');
      return;
    }
    try {
      await axios.post(`${API}/superadmin/domains`, domainForm);
      toast.success('Dominio agregado. Configurá el DNS para activarlo.');
      setShowDomainModal(false);
      setDomainForm({ custom_domain: '', business_id: '' });
      loadDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar dominio');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('¿Eliminar este dominio?')) return;
    try {
      await axios.delete(`${API}/superadmin/domains/${domainId}`);
      toast.success('Dominio eliminado');
      loadDomains();
    } catch (error) {
      toast.error('Error al eliminar dominio');
    }
  };

  const handleVerifyClientDomain = async (domainId) => {
    try {
      const res = await axios.post(`${API}/superadmin/domains/${domainId}/verify`);
      if (res.data.match) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data.message);
      }
      loadDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al verificar dominio');
    }
  };

  const handleMarkDomainActive = async (domainId) => {
    try {
      await axios.post(`${API}/superadmin/domains/${domainId}/mark-active`);
      toast.success('Dominio marcado como activo manualmente.');
      loadDomains();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // Genera el script de Cloudflare Worker para un dominio de cliente
  const generateWorkerScript = (mainDomain) => {
    const origin = mainDomain ? `https://${mainDomain}` : '[URL_DE_TU_APP]';
    return `// Cloudflare Worker - Proxy para dominio de cliente
// Pegar en: Workers & Pages > Create Worker
addEventListener('fetch', event => {
  event.respondWith(handleRequest(event.request))
})

async function handleRequest(request) {
  const url = new URL(request.url)
  const target = \`${origin}\${url.pathname}\${url.search}\`
  return await fetch(target)
}`;
  };

  // Login Screen
  if (!isAuthenticated || !isSuperAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-md w-full bg-[#1a1a1a] rounded-lg border border-[#333] p-8">
          <div className="text-center mb-8">
            <h1 
              className="text-4xl font-black mb-2"
              style={{ fontFamily: 'Unbounded, sans-serif', color: '#E9FF60' }}
              data-testid="superadmin-title"
            >
              SUPERADMIN
            </h1>
            <p className="text-gray-400 text-sm">Panel de Gestión Integral</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Usuario</label>
              <Input
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                placeholder="admin"
                required
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="superadmin-username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Contraseña</label>
              <Input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                placeholder="••••••••"
                required
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="superadmin-password"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold" 
              disabled={loading} 
              data-testid="superadmin-login-button"
            >
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
            </Button>
          </form>
          <p className="text-xs text-gray-500 text-center mt-6">
            Credenciales por defecto: admin / admin123
          </p>
        </div>
      </div>
    );
  }

  // Dashboard Layout
  return (
    <div className="flex h-screen" style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>
      {/* Sidebar */}
      <div className="w-64 bg-[#1a1a1a] border-r border-[#333] flex flex-col">
        <div className="p-6 border-b border-[#333]">
          <h1 
            className="text-2xl font-black"
            style={{ fontFamily: 'Unbounded, sans-serif', color: '#E9FF60' }}
          >
            SUPERADMIN
          </h1>
          <p className="text-xs text-gray-400 mt-1">Panel de Gestión</p>
        </div>
        
        <nav className="flex-1 p-4">
          <div className="space-y-2">
            <button
              onClick={() => setActiveSection('dashboard')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'dashboard' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="font-semibold">Dashboard</span>
            </button>
            
            <button
              onClick={() => setActiveSection('clientes')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'clientes' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-clientes"
            >
              <Users className="w-5 h-5" />
              <span className="font-semibold">Mis Clientes</span>
            </button>

            <button
              onClick={() => setActiveSection('analytics')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'analytics' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-analytics"
            >
              <BarChart3 className="w-5 h-5" />
              <span className="font-semibold">Analytics</span>
            </button>
            
            <button
              onClick={() => setActiveSection('carga-ia')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'carga-ia' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-carga-ia"
            >
              <Upload className="w-5 h-5" />
              <span className="font-semibold">Carga Masiva IA</span>
            </button>
            
            <button
              onClick={() => setActiveSection('config')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'config' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-config"
            >
              <Settings className="w-5 h-5" />
              <span className="font-semibold">Configuración</span>
            </button>

            <div className="pt-4 mt-4 border-t border-[#333]">
              <p className="text-xs text-gray-500 mb-2 px-4">EDITOR</p>
            </div>
            
            <button
              onClick={() => setActiveSection('pages')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'pages' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-pages"
            >
              <FileText className="w-5 h-5" />
              <span className="font-semibold">Editor de Páginas</span>
            </button>

            <button
              onClick={() => setActiveSection('domains')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                activeSection === 'domains' 
                  ? 'bg-[#E9FF60] text-black' 
                  : 'text-gray-400 hover:bg-[#2a2a2a]'
              }`}
              data-testid="nav-domains"
            >
              <Globe className="w-5 h-5" />
              <span className="font-semibold">Dominios</span>
            </button>
          </div>
        </nav>
        
        <div className="p-4 border-t border-[#333] space-y-2">
          <button
            onClick={() => window.location.href = '/'}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#2a2a2a] transition-all"
          >
            <Home className="w-5 h-5" />
            <span>Volver al Home</span>
          </button>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-[#2a2a2a] transition-all"
            data-testid="superadmin-logout"
          >
            <LogOut className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-[#1a1a1a] border-b border-[#333] px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold" style={{ color: '#E9FF60' }}>
                {activeSection === 'dashboard' && 'Dashboard'}
                {activeSection === 'clientes' && 'Mis Clientes'}
                {activeSection === 'analytics' && 'Analytics'}
                {activeSection === 'carga-ia' && 'Carga Masiva IA'}
                {activeSection === 'config' && 'Configuración Global'}
                {activeSection === 'pages' && 'Editor de Páginas'}
                {activeSection === 'domains' && 'Dominios Personalizados'}
              </h2>
              <p className="text-sm text-gray-400 mt-1">
                {activeSection === 'dashboard' && 'Vista general del sistema'}
                {activeSection === 'clientes' && `${businesses.length} negocios registrados`}
                {activeSection === 'analytics' && 'Métricas globales y por negocio'}
                {activeSection === 'carga-ia' && 'Importación inteligente de productos'}
                {activeSection === 'config' && 'Ajustes y personalizaciones'}
                {activeSection === 'pages' && 'Edita textos y precios de tu sitio'}
                {activeSection === 'domains' && 'Vinculá tu dominio principal y gestioná dominios de clientes'}
              </p>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto p-8">
          {/* DASHBOARD VIEW */}
          {activeSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Total Negocios</span>
                    <Users className="w-5 h-5" style={{ color: '#E9FF60' }} />
                  </div>
                  <div className="text-4xl font-black" style={{ color: '#E9FF60' }}>
                    {stats.total}
                  </div>
                </div>
                
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Activos</span>
                    <LayoutDashboard className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="text-4xl font-black text-green-500">
                    {stats.active}
                  </div>
                </div>
                
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">Nuevos (7 días)</span>
                    <Plus className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-4xl font-black text-blue-500">
                    {stats.new}
                  </div>
                </div>
              </div>

              {/* Recent Businesses */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Últimos Negocios Creados</h3>
                <div className="space-y-3">
                  {businesses?.slice(0, 5).map((business) => (
                    <div key={business.id} className="flex items-center justify-between p-3 bg-[#0a0a0a] rounded-lg">
                      <div>
                        <div className="font-semibold">{business.name}</div>
                        <div className="text-sm text-gray-400">/{business.slug}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a
                          href={`/${business.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-[#2a2a2a] rounded"
                        >
                          <Eye className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CLIENTES VIEW */}
          {activeSection === 'clientes' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold" style={{ color: '#E9FF60' }}>Gestión de Negocios</h3>
                  <p className="text-sm text-gray-400">CRUD completo con edición avanzada</p>
                </div>
                <Button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(true);
                  }}
                  className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold"
                  data-testid="create-business-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Crear Negocio
                </Button>
              </div>

              {/* Businesses Table */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#0a0a0a] border-b border-[#333]">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Negocio</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Slug</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Email</th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase">Tema</th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#333]">
                      {businesses?.map((business) => (
                        <tr key={business.id} className="hover:bg-[#2a2a2a] transition-colors" data-testid={`business-row-${business.id}`}>
                          <td className="px-6 py-4">
                            <div className="font-semibold" style={{ color: '#E9FF60' }}>{business.name}</div>
                          </td>
                          <td className="px-6 py-4">
                            <code className="text-sm text-gray-400">/{business.slug}</code>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-400">
                            {business.owner_email}
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-3 py-1 text-xs rounded-full bg-[#333] text-gray-300">
                              {business.theme_preset}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => window.open(`/${business.slug}`, '_blank')}
                                className="p-2 hover:bg-[#333] rounded transition-colors"
                                title="Ver App"
                              >
                                <Eye className="w-4 h-4 text-blue-500" />
                              </button>
                              <button
                                onClick={() => {
                                  localStorage.setItem('auth_token', 'temp');
                                  window.open(`/admin`, '_blank');
                                }}
                                className="p-2 hover:bg-[#333] rounded transition-colors"
                                title="Entrar como Admin"
                              >
                                <UserCog className="w-4 h-4 text-green-500" />
                              </button>
                              <button
                                onClick={() => openEditModal(business)}
                                className="p-2 hover:bg-[#333] rounded transition-colors"
                                title="Editar"
                                data-testid={`edit-business-${business.id}`}
                              >
                                <Edit className="w-4 h-4" style={{ color: '#E9FF60' }} />
                              </button>
                              <button
                                onClick={() => openDeleteModal(business)}
                                className="p-2 hover:bg-[#333] rounded transition-colors"
                                title="Eliminar"
                                data-testid={`delete-business-${business.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {businesses.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      No hay negocios creados aún
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ANALYTICS VIEW */}
          {activeSection === 'analytics' && (
            <div className="space-y-6">
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E9FF60]"></div>
                </div>
              ) : analyticsData ? (
                <>
                  {/* Global KPI Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                      { label: 'Negocios', value: analyticsData.total_businesses, icon: Users, color: '#E9FF60' },
                      { label: 'Pedidos Totales', value: analyticsData.total_orders, icon: Package, color: '#60a5fa' },
                      { label: 'Ingresos Totales', value: `$${analyticsData.total_revenue?.toLocaleString('es-AR')}`, icon: TrendingUp, color: '#4ade80' },
                      { label: 'Clientes Únicos', value: analyticsData.total_customers, icon: Activity, color: '#f472b6' },
                      { label: 'Pedidos Este Mes', value: analyticsData.orders_this_month, icon: BarChart3, color: '#fb923c' },
                    ].map((kpi) => (
                      <div key={kpi.label} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-5">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs text-gray-400 uppercase tracking-wide">{kpi.label}</p>
                          <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
                        </div>
                        <p className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Daily Trends Chart */}
                  {analyticsData.daily_trends?.length > 0 && (
                    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                      <h3 className="text-lg font-bold mb-6" style={{ color: '#E9FF60' }}>
                        Tendencia de Pedidos — Últimos 30 días
                      </h3>
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={analyticsData.daily_trends} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            tickFormatter={(v) => v.slice(5)}
                          />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                            labelStyle={{ color: '#E9FF60' }}
                            itemStyle={{ color: '#fff' }}
                          />
                          <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 12 }} />
                          <Bar dataKey="orders" name="Pedidos" fill="#E9FF60" radius={[4,4,0,0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Revenue Trend */}
                  {analyticsData.daily_trends?.length > 0 && (
                    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                      <h3 className="text-lg font-bold mb-6" style={{ color: '#E9FF60' }}>
                        Ingresos — Últimos 30 días
                      </h3>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={analyticsData.daily_trends} margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: '#9ca3af', fontSize: 10 }}
                            tickFormatter={(v) => v.slice(5)}
                          />
                          <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                            labelStyle={{ color: '#E9FF60' }}
                            formatter={(value) => [`$${value?.toLocaleString('es-AR')}`, 'Ingresos']}
                          />
                          <Line type="monotone" dataKey="revenue" stroke="#4ade80" strokeWidth={2} dot={false} name="Ingresos" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Per-business table */}
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                    <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>
                      Rendimiento por Negocio
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-gray-400 text-xs uppercase border-b border-[#333]">
                            <th className="text-left py-3 pr-4">Negocio</th>
                            <th className="text-right py-3 pr-4">Pedidos</th>
                            <th className="text-right py-3 pr-4">Este Mes</th>
                            <th className="text-right py-3 pr-4">Clientes</th>
                            <th className="text-right py-3 pr-4">Ingresos</th>
                            <th className="text-right py-3">Último Pedido</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#222]">
                          {analyticsBusinesses.map((biz) => (
                            <tr key={biz.id} className="hover:bg-[#2a2a2a] transition-colors">
                              <td className="py-3 pr-4">
                                <p className="font-semibold text-white">{biz.name}</p>
                                <p className="text-xs text-gray-500">/{biz.slug}</p>
                              </td>
                              <td className="text-right py-3 pr-4 font-bold" style={{ color: '#E9FF60' }}>{biz.total_orders}</td>
                              <td className="text-right py-3 pr-4 text-blue-400">{biz.orders_this_month}</td>
                              <td className="text-right py-3 pr-4 text-gray-300">{biz.total_customers}</td>
                              <td className="text-right py-3 pr-4 text-green-400 font-semibold">
                                ${biz.total_revenue?.toLocaleString('es-AR')}
                              </td>
                              <td className="text-right py-3 text-xs text-gray-500">
                                {biz.last_order_at ? new Date(biz.last_order_at).toLocaleDateString('es-AR') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {analyticsBusinesses.length === 0 && (
                        <p className="text-center py-8 text-gray-500">Sin datos aún</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-20 text-gray-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No hay datos de analytics disponibles</p>
                </div>
              )}
            </div>
          )}

          {/* CARGA IA VIEW */}
          {activeSection === 'carga-ia' && (
            <div className="space-y-6">
              {/* Upload Area */}
              {!uploadedFile && parsedProducts.length === 0 && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-8">
                  <div className="text-center mb-6">
                    <Upload className="w-16 h-16 mx-auto mb-4" style={{ color: '#E9FF60' }} />
                    <h3 className="text-xl font-bold mb-2" style={{ color: '#E9FF60' }}>
                      Importación Inteligente de Productos
                    </h3>
                    <p className="text-gray-400">
                      Arrastra un archivo CSV o TXT para importar productos automáticamente
                    </p>
                  </div>
                  
                  <div 
                    className={`border-2 border-dashed rounded-lg p-12 transition-all cursor-pointer ${
                      isDragging 
                        ? 'border-[#E9FF60] bg-[#E9FF60]/10' 
                        : 'border-[#333] hover:border-[#E9FF60]/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-upload').click()}
                  >
                    <div className="text-center">
                      <div className="text-4xl mb-3">📁</div>
                      <div className="text-lg font-semibold mb-2" style={{ color: '#E9FF60' }}>
                        Arrastra tu archivo aquí
                      </div>
                      <div className="text-sm text-gray-500">
                        o haz clic para seleccionar
                      </div>
                      <div className="text-xs text-gray-600 mt-2">
                        Formatos aceptados: CSV, TXT
                      </div>
                    </div>
                  </div>
                  
                  <input
                    id="file-upload"
                    type="file"
                    accept=".csv,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  <div className="mt-6 bg-[#0a0a0a] rounded-lg p-6">
                    <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#E9FF60' }}>
                      <span>📋</span> Formato esperado:
                    </h4>
                    <div className="text-sm text-gray-400 space-y-2 mb-4">
                      <p>El archivo debe tener las columnas en este orden (con o sin encabezados):</p>
                      <ol className="list-decimal list-inside space-y-1 ml-2">
                        <li><strong>Categoría:</strong> Nombre de la categoría</li>
                        <li><strong>Nombre:</strong> Nombre del producto</li>
                        <li><strong>Precio:</strong> Precio en números (ej: 1200)</li>
                        <li><strong>Descripción:</strong> Detalles del producto (opcional)</li>
                        <li><strong>URL Imagen:</strong> Link de la imagen (opcional)</li>
                      </ol>
                    </div>
                    <div className="p-4 bg-[#1a1a1a] rounded font-mono text-xs text-gray-300 overflow-x-auto">
                      <div className="mb-2 text-[#E9FF60]">Ejemplo CSV:</div>
                      <div>Categoría,Nombre,Precio,Descripción</div>
                      <div>Hamburguesas,Classic Burger,1200,Carne 150g con queso</div>
                      <div>Hamburguesas,Doble Cheese,1800,Doble carne y queso</div>
                      <div>Bebidas,Coca Cola,400,Gaseosa 500ml</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Processing State */}
              {isProcessing && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-12 text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4" style={{ borderColor: '#E9FF60' }}></div>
                  <p className="text-gray-400">Procesando archivo...</p>
                </div>
              )}

              {/* Preview Table */}
              {!isProcessing && parsedProducts.length > 0 && (
                <div className="space-y-6">
                  <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-bold" style={{ color: '#E9FF60' }}>
                          Vista Previa de Importación
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          {parsedProducts.length} productos detectados - Revisa y edita antes de importar
                        </p>
                      </div>
                      <Button
                        onClick={() => {
                          setUploadedFile(null);
                          setParsedProducts([]);
                        }}
                        variant="outline"
                        className="border-[#333]"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                    </div>

                    {/* Business Selector */}
                    <div className="mb-6 p-4 bg-[#0a0a0a] rounded-lg">
                      <label className="block text-sm font-medium mb-2" style={{ color: '#E9FF60' }}>
                        Negocio Destino *
                      </label>
                      <select
                        value={selectedBusinessId}
                        onChange={(e) => setSelectedBusinessId(e.target.value)}
                        className="w-full px-4 py-3 bg-[#1a1a1a] border border-[#333] rounded-lg text-white"
                      >
                        <option value="">Selecciona un negocio...</option>
                        {businesses.map(b => (
                          <option key={b.id} value={b.id}>{b.name} (/{b.slug})</option>
                        ))}
                      </select>
                    </div>

                    {/* Products Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#0a0a0a]">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Categoría</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Nombre</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Precio</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Descripción</th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[#333]">
                          {parsedProducts.map((product) => (
                            <tr key={product.id} className="hover:bg-[#2a2a2a]">
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={product.category}
                                  onChange={(e) => updateParsedProduct(product.id, 'category', e.target.value)}
                                  className="w-full px-2 py-1 bg-[#0a0a0a] border border-[#333] rounded text-sm text-white"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={product.name}
                                  onChange={(e) => updateParsedProduct(product.id, 'name', e.target.value)}
                                  className="w-full px-2 py-1 bg-[#0a0a0a] border border-[#333] rounded text-sm text-white"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="number"
                                  value={product.price}
                                  onChange={(e) => updateParsedProduct(product.id, 'price', parseFloat(e.target.value))}
                                  className="w-24 px-2 py-1 bg-[#0a0a0a] border border-[#333] rounded text-sm text-white"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <input
                                  type="text"
                                  value={product.description}
                                  onChange={(e) => updateParsedProduct(product.id, 'description', e.target.value)}
                                  className="w-full px-2 py-1 bg-[#0a0a0a] border border-[#333] rounded text-sm text-white"
                                />
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  onClick={() => removeParsedProduct(product.id)}
                                  className="p-2 hover:bg-[#333] rounded transition-colors"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <Button
                        onClick={handleImportProducts}
                        disabled={!selectedBusinessId || isProcessing}
                        className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Importar {parsedProducts.length} Productos
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CONFIG VIEW */}
          {activeSection === 'config' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                {/* Fuentes */}
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>
                    Fuentes Disponibles
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">
                    Formatos permitidos: <code className="text-[#E9FF60]">.otf</code>, <code className="text-[#E9FF60]">.ttf</code>, <code className="text-[#E9FF60]">.woff</code>, <code className="text-[#E9FF60]">.woff2</code>
                  </p>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {fonts.map((font) => (
                      <div key={font.id} className="p-3 bg-[#0a0a0a] rounded flex items-center justify-between">
                        <div>
                          <span style={{ fontFamily: font.name }}>{font.name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            {font.source === 'google' ? 'Google Fonts' : 'Custom'}
                          </span>
                        </div>
                        {font.source === 'custom' && (
                          <button
                            onClick={() => handleDeleteFont(font.id)}
                            className="p-1 hover:bg-[#333] rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={() => setShowFontModal(true)}
                    className="w-full mt-4 bg-[#E9FF60] hover:bg-[#f0ff80] text-black"
                    data-testid="add-font-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Nueva Fuente
                  </Button>
                </div>

                {/* Presets de Color */}
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>
                    Presets de Colores
                  </h3>
                  <p className="text-xs text-gray-400 mb-4">
                    Paletas predefinidas para aplicar a negocios
                  </p>
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {/* Presets predefinidos */}
                    <div className="p-3 bg-[#0a0a0a] rounded">
                      <div className="font-semibold mb-2">Elegante Dorado <span className="text-xs text-gray-500">(Sistema)</span></div>
                      <div className="flex gap-2">
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#FFD700' }} title="#FFD700"></div>
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#0D0D0D' }} title="#0D0D0D"></div>
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#262626' }} title="#262626"></div>
                      </div>
                    </div>
                    <div className="p-3 bg-[#0a0a0a] rounded">
                      <div className="font-semibold mb-2">Moderno Naranja <span className="text-xs text-gray-500">(Sistema)</span></div>
                      <div className="flex gap-2">
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#FF6B35' }} title="#FF6B35"></div>
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#1a1a1a' }} title="#1a1a1a"></div>
                        <div className="w-10 h-10 rounded" style={{ backgroundColor: '#333333' }} title="#333333"></div>
                      </div>
                    </div>
                    {/* Presets custom desde DB */}
                    {presets.map((preset) => (
                      <div key={preset.id} className="p-3 bg-[#0a0a0a] rounded">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold">{preset.name}</div>
                          <button
                            onClick={() => handleDeletePreset(preset.id)}
                            className="p-1 hover:bg-[#333] rounded transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                        <div className="flex gap-2">
                          <div className="w-10 h-10 rounded" style={{ backgroundColor: preset.primary_color }} title={preset.primary_color}></div>
                          <div className="w-10 h-10 rounded" style={{ backgroundColor: preset.accent_color }} title={preset.accent_color}></div>
                          <div className="w-10 h-10 rounded" style={{ backgroundColor: preset.background_color }} title={preset.background_color}></div>
                          <div className="w-10 h-10 rounded border border-gray-600" style={{ backgroundColor: preset.text_color }} title={preset.text_color}></div>
                          <div className="w-10 h-10 rounded" style={{ backgroundColor: preset.card_color }} title={preset.card_color}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button 
                    onClick={() => setShowPresetModal(true)}
                    className="w-full mt-4 bg-[#E9FF60] hover:bg-[#f0ff80] text-black"
                    data-testid="add-preset-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Crear Nuevo Preset
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* PAGES EDITOR VIEW */}
          {activeSection === 'pages' && (
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-6">
                {/* Page Selector */}
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-4">
                  <h3 className="text-sm font-bold mb-4" style={{ color: '#E9FF60' }}>Páginas</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => handleSelectPage('settings')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedPage === 'settings' ? 'bg-[#E9FF60] text-black' : 'hover:bg-[#2a2a2a]'
                      }`}
                    >
                      <Settings className="w-4 h-4 inline mr-2" />
                      Configuración Global
                    </button>
                    <button
                      onClick={() => handleSelectPage('home')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedPage === 'home' ? 'bg-[#E9FF60] text-black' : 'hover:bg-[#2a2a2a]'
                      }`}
                    >
                      <FileText className="w-4 h-4 inline mr-2" />
                      Home
                    </button>
                    <button
                      onClick={() => handleSelectPage('soluciones')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedPage === 'soluciones' ? 'bg-[#E9FF60] text-black' : 'hover:bg-[#2a2a2a]'
                      }`}
                    >
                      <FileText className="w-4 h-4 inline mr-2" />
                      Soluciones
                    </button>
                    <button
                      onClick={() => handleSelectPage('planes')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedPage === 'planes' ? 'bg-[#E9FF60] text-black' : 'hover:bg-[#2a2a2a]'
                      }`}
                    >
                      <DollarSign className="w-4 h-4 inline mr-2" />
                      Planes y Precios
                    </button>
                    <button
                      onClick={() => handleSelectPage('demo')}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedPage === 'demo' ? 'bg-[#E9FF60] text-black' : 'hover:bg-[#2a2a2a]'
                      }`}
                    >
                      <FileText className="w-4 h-4 inline mr-2" />
                      Solicitá Demo
                    </button>
                  </div>
                </div>

                {/* Content Editor */}
                <div className="col-span-3">
                  {!selectedPage ? (
                    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-12 text-center">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-gray-400">Selecciona una página para editar</p>
                    </div>
                  ) : selectedPage === 'settings' && pageContent ? (
                    /* SETTINGS EDITOR */
                    <div className="space-y-6">
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>
                          Configuración Global del Sistema
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Nombre de la Marca</label>
                            <Input
                              value={pageContent.brand_name || ''}
                              onChange={(e) => setPageContent({ ...pageContent, brand_name: e.target.value })}
                              placeholder="mispedidos app"
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                            <p className="text-xs text-gray-500 mt-1">Se muestra en navbar, footer y títulos</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Dominio Principal</label>
                            <Input
                              value={pageContent.main_domain || ''}
                              onChange={(e) => setPageContent({ ...pageContent, main_domain: e.target.value })}
                              placeholder="mispedidosapp.com.ar"
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">WhatsApp (sin +)</label>
                            <Input
                              value={pageContent.whatsapp_number || ''}
                              onChange={(e) => setPageContent({ ...pageContent, whatsapp_number: e.target.value })}
                              placeholder="3425149602"
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Email de Contacto</label>
                            <Input
                              value={pageContent.contact_email || ''}
                              onChange={(e) => setPageContent({ ...pageContent, contact_email: e.target.value })}
                              placeholder="mispedidosapp@gmail.com"
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                      </div>
                      <Button onClick={handleSavePage} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Configuración
                      </Button>

                      {/* Google Sheets Config */}
                      <GoogleSheetsConfigSection API={API} axios={axios} toast={toast} />
                    </div>
                  ) : selectedPage === 'home' && pageContent ? (
                    /* HOME EDITOR */
                    <div className="space-y-6">
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Hero - Títulos</h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Título 1 (amarillo)</label>
                            <Input value={pageContent.hero?.title_1 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_1: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Título 2 (blanco)</label>
                            <Input value={pageContent.hero?.title_2 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_2: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Título 3 (amarillo)</label>
                            <Input value={pageContent.hero?.title_3 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_3: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Hero - Taglines</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Tagline 1 (destacado)</label>
                            <Input value={pageContent.hero?.tagline_1 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, tagline_1: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Tagline 2</label>
                            <Input value={pageContent.hero?.tagline_2 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, tagline_2: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Tagline 3</label>
                            <Input value={pageContent.hero?.tagline_3 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, tagline_3: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Pregunta</label>
                            <Input value={pageContent.hero?.question || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, question: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Texto Botón CTA</label>
                              <Input value={pageContent.hero?.cta_text || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, cta_text: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Link CTA</label>
                              <Input value={pageContent.hero?.cta_link || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, cta_link: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Features</h3>
                        <div>
                          <label className="block text-sm font-medium mb-2 text-gray-300">Título de la Sección</label>
                          <Input value={pageContent.features?.title || ''} onChange={(e) => setPageContent({ ...pageContent, features: { ...pageContent.features, title: e.target.value } })} className="bg-[#0a0a0a] border-[#333] text-white mb-4" />
                        </div>
                        {pageContent.features?.items?.map((item, i) => (
                          <div key={i} className="p-4 bg-[#0a0a0a] rounded-lg mb-3">
                            <div className="grid grid-cols-4 gap-3">
                              <Input value={item.emoji} onChange={(e) => { const newItems = [...pageContent.features.items]; newItems[i].emoji = e.target.value; setPageContent({ ...pageContent, features: { ...pageContent.features, items: newItems } }); }} placeholder="Emoji" className="bg-[#1a1a1a] border-[#333] text-white" />
                              <Input value={item.title} onChange={(e) => { const newItems = [...pageContent.features.items]; newItems[i].title = e.target.value; setPageContent({ ...pageContent, features: { ...pageContent.features, items: newItems } }); }} placeholder="Título" className="col-span-3 bg-[#1a1a1a] border-[#333] text-white" />
                            </div>
                            <Textarea value={item.description} onChange={(e) => { const newItems = [...pageContent.features.items]; newItems[i].description = e.target.value; setPageContent({ ...pageContent, features: { ...pageContent.features, items: newItems } }); }} rows={2} className="mt-2 bg-[#1a1a1a] border-[#333] text-white" />
                          </div>
                        ))}
                      </div>
                      <Button onClick={handleSavePage} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Home
                      </Button>
                    </div>
                  ) : selectedPage === 'soluciones' && pageContent ? (
                    /* SOLUCIONES EDITOR */
                    <div className="space-y-6">
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Hero</h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-3 gap-4">
                            <Input value={pageContent.hero?.title_1 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_1: e.target.value } })} placeholder="Título 1" className="bg-[#0a0a0a] border-[#333] text-white" />
                            <Input value={pageContent.hero?.title_2 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_2: e.target.value } })} placeholder="Título 2" className="bg-[#0a0a0a] border-[#333] text-white" />
                            <Input value={pageContent.hero?.title_3 || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, title_3: e.target.value } })} placeholder="Título 3" className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-400">Subtítulo 1 (gris)</label>
                            <Input value={pageContent.hero?.subtitle || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, subtitle: e.target.value } })} placeholder="No somos un marketplace. Somos una " className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1 text-gray-400">Subtítulo 2 (amarillo)</label>
                            <Input value={pageContent.hero?.subtitle_accent || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, subtitle_accent: e.target.value } })} placeholder="fábrica de soberanía digital" className="bg-[#0a0a0a] border-[#E9FF60] text-[#E9FF60]" />
                          </div>
                          <Textarea value={pageContent.hero?.description || ''} onChange={(e) => setPageContent({ ...pageContent, hero: { ...pageContent.hero, description: e.target.value } })} rows={2} placeholder="Descripción" className="bg-[#0a0a0a] border-[#333] text-white" />
                        </div>
                      </div>
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Value Props</h3>
                        <p className="text-xs text-gray-500 mb-4">Los íconos se mantienen automáticamente. Editá título y descripción de cada bloque.</p>
                        {(pageContent.value_props?.length > 0 ? pageContent.value_props : [
                          {icon: '0%', title: '', description: ''},
                          {icon: 'target', title: '', description: ''},
                          {icon: 'trending', title: '', description: ''}
                        ]).map((prop, i) => {
                          const labels = ['Bloque 1 (ícono: 0%)', 'Bloque 2 (ícono: flecha)', 'Bloque 3 (ícono: tendencia)'];
                          const currentProps = pageContent.value_props?.length > 0 ? pageContent.value_props : [
                            {icon: '0%', title: '', description: ''},
                            {icon: 'target', title: '', description: ''},
                            {icon: 'trending', title: '', description: ''}
                          ];
                          return (
                          <div key={i} className="p-4 bg-[#0a0a0a] rounded-lg mb-3">
                            <p className="text-xs text-gray-500 mb-2 font-semibold">{labels[i]}</p>
                            <Input value={prop.title} onChange={(e) => { const newProps = [...currentProps]; newProps[i] = {...newProps[i], title: e.target.value}; setPageContent({ ...pageContent, value_props: newProps }); }} placeholder="Título" className="bg-[#1a1a1a] border-[#333] text-white mb-2" />
                            <Textarea value={prop.description} onChange={(e) => { const newProps = [...currentProps]; newProps[i] = {...newProps[i], description: e.target.value}; setPageContent({ ...pageContent, value_props: newProps }); }} rows={2} placeholder="Descripción" className="bg-[#1a1a1a] border-[#333] text-white" />
                          </div>
                          );
                        })}
                      </div>
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Estrategia</h3>
                        <Input value={pageContent.strategy?.title || ''} onChange={(e) => setPageContent({ ...pageContent, strategy: { ...pageContent.strategy, title: e.target.value } })} placeholder="Título" className="bg-[#0a0a0a] border-[#333] text-white mb-3" />
                        <Textarea value={pageContent.strategy?.description || ''} onChange={(e) => setPageContent({ ...pageContent, strategy: { ...pageContent.strategy, description: e.target.value } })} rows={2} className="bg-[#0a0a0a] border-[#333] text-white mb-3" />
                        <Textarea value={pageContent.strategy?.items?.join('\n') || ''} onChange={(e) => setPageContent({ ...pageContent, strategy: { ...pageContent.strategy, items: e.target.value.split('\n').filter(i => i.trim()) } })} rows={4} placeholder="Items (uno por línea)" className="bg-[#0a0a0a] border-[#333] text-white" />
                      </div>
                      <Button onClick={handleSavePage} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Soluciones
                      </Button>
                    </div>
                  ) : selectedPage === 'planes' && pageContent ? (
                    <div className="space-y-6">
                      {/* Pricing Header */}
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>
                          Configuración de Precios
                        </h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Título</label>
                              <Input
                                value={pageContent.title || ''}
                                onChange={(e) => setPageContent({ ...pageContent, title: e.target.value })}
                                className="bg-[#0a0a0a] border-[#333] text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Moneda a Mostrar</label>
                              <select
                                value={pageContent.currency || 'ARS'}
                                onChange={(e) => setPageContent({ ...pageContent, currency: e.target.value })}
                                className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                              >
                                <option value="ARS">Pesos Argentinos (ARS)</option>
                                <option value="USD">Dólares (USD)</option>
                              </select>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Subtítulo</label>
                            <Input
                              value={pageContent.subtitle || ''}
                              onChange={(e) => setPageContent({ ...pageContent, subtitle: e.target.value })}
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Plans */}
                      {pageContent.plans?.map((plan, i) => (
                        <div key={plan.id || i} className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-bold" style={{ color: '#E9FF60' }}>
                              Plan: {plan.name}
                              {plan.is_popular && <span className="ml-2 px-2 py-1 text-xs bg-[#E9FF60] text-black rounded">Popular</span>}
                            </h4>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={plan.is_popular || false}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].is_popular = e.target.checked;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                className="rounded"
                              />
                              Popular
                            </label>
                          </div>
                          
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Nombre</label>
                              <Input
                                value={plan.name}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].name = e.target.value;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                className="bg-[#0a0a0a] border-[#333] text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Precio ARS</label>
                              <Input
                                type="number"
                                value={plan.price_ars}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].price_ars = parseFloat(e.target.value) || 0;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                className="bg-[#0a0a0a] border-[#333] text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Precio USD</label>
                              <Input
                                type="number"
                                value={plan.price_usd}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].price_usd = parseFloat(e.target.value) || 0;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                className="bg-[#0a0a0a] border-[#333] text-white"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Texto del Botón</label>
                              <Input
                                value={plan.button_text}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].button_text = e.target.value;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                className="bg-[#0a0a0a] border-[#333] text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">
                                Link Botón — <span className="text-green-400">USD</span>
                              </label>
                              <Input
                                value={plan.button_link_usd || plan.button_link || ''}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].button_link_usd = e.target.value;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                placeholder="https://mpago.la/pago-usd-..."
                                className="bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-sm font-medium mb-2 text-gray-300">
                                Link Botón — <span className="text-blue-400">Pesos (ARS)</span>
                              </label>
                              <Input
                                value={plan.button_link_ars || plan.button_link || ''}
                                onChange={(e) => {
                                  const newPlans = [...pageContent.plans];
                                  newPlans[i].button_link_ars = e.target.value;
                                  setPageContent({ ...pageContent, plans: newPlans });
                                }}
                                placeholder="https://mpago.la/pago-pesos-..."
                                className="bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Features (una por línea)</label>
                            <Textarea
                              value={plan.features?.join('\n') || ''}
                              onChange={(e) => {
                                const newPlans = [...pageContent.plans];
                                newPlans[i].features = e.target.value.split('\n').filter(f => f.trim());
                                setPageContent({ ...pageContent, plans: newPlans });
                              }}
                              rows={4}
                              className="bg-[#0a0a0a] border-[#333] text-white"
                            />
                          </div>
                        </div>
                      ))}

                      <Button onClick={handleSavePage} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Planes
                      </Button>
                    </div>
                  ) : selectedPage === 'demo' && pageContent ? (
                    /* DEMO EDITOR */
                    <div className="space-y-6">
                      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-4" style={{ color: '#E9FF60' }}>Página de Demo</h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Título 1</label>
                              <Input value={pageContent.title_1 || ''} onChange={(e) => setPageContent({ ...pageContent, title_1: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium mb-2 text-gray-300">Título 2</label>
                              <Input value={pageContent.title_2 || ''} onChange={(e) => setPageContent({ ...pageContent, title_2: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Subtítulo</label>
                            <Textarea value={pageContent.subtitle || ''} onChange={(e) => setPageContent({ ...pageContent, subtitle: e.target.value })} rows={2} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Título de Éxito</label>
                            <Input value={pageContent.success_title || ''} onChange={(e) => setPageContent({ ...pageContent, success_title: e.target.value })} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Mensaje de Éxito</label>
                            <Textarea value={pageContent.success_message || ''} onChange={(e) => setPageContent({ ...pageContent, success_message: e.target.value })} rows={2} className="bg-[#0a0a0a] border-[#333] text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Título de Error</label>
                            <Input value={pageContent.error_title || ''} onChange={(e) => setPageContent({ ...pageContent, error_title: e.target.value })} placeholder="Error al enviar" className="bg-[#0a0a0a] border-red-900 text-white" />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2 text-gray-300">Mensaje de Error</label>
                            <Textarea value={pageContent.error_message || ''} onChange={(e) => setPageContent({ ...pageContent, error_message: e.target.value })} rows={2} placeholder="Error al enviar la solicitud. Intentá de nuevo." className="bg-[#0a0a0a] border-red-900 text-white" />
                          </div>
                        </div>
                      </div>
                      <Button onClick={handleSavePage} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold">
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Demo
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-12 text-center">
                      <p className="text-gray-400">Cargando contenido...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* DOMAINS VIEW */}
          {activeSection === 'domains' && (
            <div className="space-y-6">

              {/* SERVER IP CARD */}
              <div className="bg-[#1a1a1a] border border-[#E9FF60]/20 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Server className="w-5 h-5 text-[#E9FF60]" />
                  <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>IP del Servidor Actual</h3>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  Esta es la dirección IP de <strong className="text-white">ingreso</strong> del servidor — a la que debés apuntar tus registros DNS (tipo A).
                </p>
                <div className="flex items-center gap-3 mb-4">
                  <div 
                    className="flex-1 font-mono text-xl font-bold p-4 bg-[#0a0a0a] rounded-lg border border-[#333] text-[#E9FF60]"
                    data-testid="server-ip-display"
                  >
                    {serverIP || <span className="text-gray-500 text-base">Cargando IP...</span>}
                  </div>
                  {serverIP && (
                    <button
                      onClick={() => { navigator.clipboard.writeText(serverIP); toast.success('IP copiada'); }}
                      className="p-4 bg-[#2a2a2a] hover:bg-[#333] rounded-lg border border-[#333] transition-all"
                      title="Copiar IP"
                      data-testid="copy-server-ip"
                    >
                      <Copy className="w-5 h-5 text-gray-400" />
                    </button>
                  )}
                  <button
                    onClick={loadServerIP}
                    className="p-4 bg-[#2a2a2a] hover:bg-[#333] rounded-lg border border-[#333] transition-all"
                    title="Refrescar IP"
                  >
                    <RefreshCw className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* Managed cloud notice */}
                {isManagedCloud && (
                  <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-blue-400 mb-1">Entorno Cloud Gestionado (Kubernetes)</p>
                        <p className="text-xs text-gray-400">
                          Este servidor corre en una infraestructura cloud gestionada. La IP mostrada es la de acceso desde internet. 
                          Para producción en <strong className="text-white">VPS propio</strong>, esta IP es la que debés usar en el registro DNS. 
                          Para dominios en plataformas cloud gestionadas, contactá al soporte de tu plataforma de hosting.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* MAIN DOMAIN CONFIG */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Globe className="w-5 h-5 text-[#E9FF60]" />
                  <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>Dominio Principal del Sistema</h3>
                  {/* Status Badge */}
                  {mainDomainConfig.status === 'active' && (
                    <span className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30" data-testid="domain-status-active">
                      <CheckCircle className="w-3.5 h-3.5" /> ACTIVO
                    </span>
                  )}
                  {mainDomainConfig.status === 'pending' && (
                    <span className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30" data-testid="domain-status-pending">
                      <Clock className="w-3.5 h-3.5" /> VERIFICANDO
                    </span>
                  )}
                  {(mainDomainConfig.status === 'not_set' || !mainDomainConfig.status) && (
                    <span className="ml-auto flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gray-500/20 text-gray-400 border border-gray-500/30" data-testid="domain-status-not-set">
                      <AlertCircle className="w-3.5 h-3.5" /> NO VINCULADO
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400 mb-4">
                  Ingresá el dominio principal de tu plataforma (ej: <span className="text-[#E9FF60] font-mono">mispedidos.com</span>). 
                  Una vez activo, los negocios se accederán como <span className="text-[#E9FF60] font-mono">mispedidos.com/<strong>slug-cliente</strong></span>.
                </p>

                <div className="flex gap-3 mb-4">
                  <div className="flex-1">
                    <Input
                      value={mainDomainInput}
                      onChange={(e) => setMainDomainInput(e.target.value)}
                      placeholder="midominio.com"
                      className="bg-[#0a0a0a] border-[#333] text-white font-mono"
                      data-testid="main-domain-input"
                    />
                  </div>
                  <Button
                    onClick={handleSaveMainDomain}
                    className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold"
                    data-testid="save-main-domain-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                  {mainDomainConfig.domain && (
                    <Button
                      onClick={handleVerifyMainDomain}
                      disabled={isVerifyingDomain}
                      variant="outline"
                      className="border-[#E9FF60] text-[#E9FF60] hover:bg-[#E9FF60]/10"
                      data-testid="verify-domain-btn"
                    >
                      {isVerifyingDomain ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-4 h-4 mr-2" />
                      )}
                      {isVerifyingDomain ? 'Verificando...' : 'Verificar DNS'}
                    </Button>
                  )}
                </div>

                {/* Last verified info */}
                {mainDomainConfig.last_verified && (
                  <p className="text-xs text-gray-500 mb-4">
                    Última verificación: {new Date(mainDomainConfig.last_verified).toLocaleString('es-AR')} — 
                    {mainDomainConfig.last_verified_ok 
                      ? <span className="text-green-400"> Conexión exitosa</span>
                      : <span className="text-yellow-400"> Sin coincidencia</span>
                    }
                  </p>
                )}

                {/* Verify Result */}
                {verifyResult && (
                  <div className={`p-4 rounded-lg border mb-4 ${verifyResult.match ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                    <div className="flex items-start gap-2">
                      {verifyResult.match 
                        ? <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                        : <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
                      }
                      <div>
                        <p className={`text-sm font-medium ${verifyResult.match ? 'text-green-400' : 'text-yellow-400'}`}>
                          {verifyResult.message}
                        </p>
                        {verifyResult.resolved_ips?.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            IPs resueltas: <span className="font-mono">{verifyResult.resolved_ips.join(', ')}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* DNS SETUP INSTRUCTIONS */}
              {mainDomainConfig.domain && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertTriangle className="w-5 h-5 text-[#E9FF60]" />
                    <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>Configuración DNS Requerida</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    En el panel de tu proveedor de dominio (GoDaddy, Namecheap, NIC.ar, etc.), agregá el siguiente registro DNS:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm mb-4">
                      <thead>
                        <tr className="text-gray-500 text-xs uppercase">
                          <th className="text-left pb-2 pr-4">Tipo</th>
                          <th className="text-left pb-2 pr-4">Host / Nombre</th>
                          <th className="text-left pb-2 pr-4">Valor / Apunta a</th>
                          <th className="text-left pb-2">TTL</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="font-mono">
                          <td className="pr-4 py-2">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-bold">A</span>
                          </td>
                          <td className="pr-4 py-2 text-white">@</td>
                          <td className="pr-4 py-2 text-[#E9FF60] font-bold">{serverIP || '[IP del servidor]'}</td>
                          <td className="py-2 text-gray-400">3600</td>
                        </tr>
                        <tr className="font-mono">
                          <td className="pr-4 py-2">
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-bold">A</span>
                          </td>
                          <td className="pr-4 py-2 text-white">www</td>
                          <td className="pr-4 py-2 text-[#E9FF60] font-bold">{serverIP || '[IP del servidor]'}</td>
                          <td className="py-2 text-gray-400">3600</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-gray-500">
                    Los cambios DNS pueden tardar hasta 48 horas en propagarse. Una vez propagados, hacé clic en "Verificar DNS".
                  </p>
                </div>
              )}

              {/* SSL INSTRUCTIONS */}
              {mainDomainConfig.domain && (
                <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <ShieldCheck className="w-5 h-5 text-[#E9FF60]" />
                    <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>SSL y Servidor Web</h3>
                  </div>
                  <p className="text-sm text-gray-400 mb-4">
                    Para que el dominio funcione con HTTPS, configurá tu servidor web como Host Principal:
                  </p>

                  {/* Caddy */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-white">Opción 1 — Caddy (recomendado, SSL automático para todos los dominios)</p>
                      <button
                        onClick={() => {
                          const d = mainDomainConfig.domain;
                          const caddyConfig = `{
  on_demand_tls {
    ask http://localhost:8001/api/public/domain-check
    interval 2m
    burst 5
  }
}

${d} www.${d} {
  reverse_proxy localhost:3000
  reverse_proxy /api/* localhost:8001
}

:443 {
  tls {
    on_demand
  }
  reverse_proxy localhost:3000
  reverse_proxy /api/* localhost:8001
}`;
                          navigator.clipboard.writeText(caddyConfig);
                          toast.success('Copiado al portapapeles');
                        }}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copiar
                      </button>
                    </div>
                    <pre className="bg-[#0a0a0a] border border-[#333] rounded p-4 text-xs font-mono text-green-400 overflow-x-auto whitespace-pre">{`{
  on_demand_tls {
    # Verifica que el dominio esté registrado antes de emitir SSL
    ask http://localhost:8001/api/public/domain-check
    interval 2m
    burst 5
  }
}

${mainDomainConfig.domain} www.${mainDomainConfig.domain} {
  reverse_proxy localhost:3000
  reverse_proxy /api/* localhost:8001
}

# Acepta cualquier dominio de cliente con SSL automático
:443 {
  tls {
    on_demand
  }
  reverse_proxy localhost:3000
  reverse_proxy /api/* localhost:8001
}`}</pre>
                    <p className="text-xs text-gray-600 mt-1">Con <code className="text-gray-400">on_demand_tls</code>, Caddy emite certificados SSL automáticamente para cada nuevo dominio de cliente que esté registrado en el sistema.</p>
                  </div>

                  {/* Nginx */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold text-white">Opción 2 — Nginx + Certbot (acepta todos los dominios)</p>
                      <button
                        onClick={() => {
                          const config = `server {
    listen 80 default_server;
    server_name _;
    return 301 https://$host$request_uri;
}

server {
    listen 443 ssl;
    server_name ${mainDomainConfig.domain} www.${mainDomainConfig.domain};
    ssl_certificate /etc/letsencrypt/live/${mainDomainConfig.domain}/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/${mainDomainConfig.domain}/privkey.pem;

    location /api/ {
        proxy_pass http://localhost:8001/api/;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Custom-Domain $host;
    }
    location / {
        proxy_pass http://localhost:3000;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }
}

# Para dominios de clientes (requiere certbot por dominio)
# certbot certonly --nginx -d dominio-cliente.com.ar`;
                          navigator.clipboard.writeText(config);
                          toast.success('Copiado al portapapeles');
                        }}
                        className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Copiar
                      </button>
                    </div>
                    <pre className="bg-[#0a0a0a] border border-[#333] rounded p-4 text-xs font-mono text-blue-400 overflow-x-auto whitespace-pre">{`server {
    listen 80 default_server;
    server_name _;
    return 301 https://$host$request_uri;
}

server {
    listen 443 ssl;
    server_name ${mainDomainConfig.domain} www.${mainDomainConfig.domain};
    ssl_certificate /etc/letsencrypt/live/${mainDomainConfig.domain}/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/${mainDomainConfig.domain}/privkey.pem;

    location /api/ {
        proxy_pass http://localhost:8001/api/;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Custom-Domain $host;
    }
    location / {
        proxy_pass http://localhost:3000;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
    }
}

# Para dominios de clientes:
# certbot certonly --nginx -d dominio-cliente.com.ar`}</pre>
                  </div>

                  {/* Slug routing note */}
                  <div className="mt-4 p-4 bg-[#E9FF60]/5 border border-[#E9FF60]/20 rounded-lg">
                    <p className="text-xs text-[#E9FF60] font-bold mb-1">Estructura de slugs habilitada</p>
                    <p className="text-xs text-gray-400">
                      Una vez activo el dominio, las rutas de tus clientes funcionarán automáticamente como:
                    </p>
                    <p className="text-xs font-mono text-white mt-1">
                      <span className="text-[#E9FF60]">{mainDomainConfig.domain}</span>/<span className="text-gray-400">slug-cliente</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Por ejemplo: <span className="font-mono">{mainDomainConfig.domain}/burger-house</span>
                    </p>
                  </div>
                </div>
              )}

              {/* CLIENT DOMAINS */}
              <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold" style={{ color: '#E9FF60' }}>Dominios Personalizados de Negocios</h3>
                  <Button 
                    onClick={() => setShowDomainModal(true)} 
                    className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black"
                    data-testid="add-client-domain-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Dominio de Negocio
                  </Button>
                </div>

                {/* Explicación de arquitectura */}
                <div className="mb-5 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-300 font-semibold mb-1">¿Cómo funcionan los dominios de clientes?</p>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Cada cliente apunta su dominio a esta plataforma usando un <strong className="text-white">Cloudflare Worker</strong>.
                    El Worker proxea el sitio de <span className="font-mono text-[#E9FF60]">{mainDomainConfig?.domain || 'tu-dominio.com.ar'}</span> de forma transparente,
                    y la app detecta automáticamente qué negocio mostrar según el dominio del cliente.
                    <br /><strong className="text-yellow-400">No se necesita A record directo al servidor</strong> — el Worker de Cloudflare hace todo el trabajo.
                  </p>
                </div>
                
                {domains.filter(d => d.business_id).length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay dominios de negocios configurados aún</p>
                ) : (
                  <div className="space-y-4">
                    {domains.filter(d => d.business_id).map((domain) => {
                      const biz = businesses.find(b => b.id === domain.business_id);
                      return (
                        <div key={domain.id} className="border border-[#333] rounded-xl overflow-hidden">
                          {/* Header del dominio */}
                          <div className="flex items-center justify-between px-4 py-3 bg-[#0a0a0a]">
                            <div>
                              <p className="font-medium font-mono text-white text-sm">{domain.custom_domain}</p>
                              <p className="text-xs text-gray-500">
                                Negocio: <span className="text-[#E9FF60]">{biz?.name || domain.business_id}</span>
                                {domain.match_type && <span className="ml-2 text-gray-600">• DNS: {domain.match_type}</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${domain.is_verified ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                {domain.is_verified ? '✓ Activo' : '⏳ Pendiente'}
                              </span>
                              {!domain.is_verified && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleVerifyClientDomain(domain.id)}
                                  className="text-blue-400 hover:text-white text-xs"
                                  data-testid={`verify-domain-${domain.id}`}
                                >
                                  <ShieldCheck className="w-3 h-3 mr-1" /> Verificar DNS
                                </Button>
                              )}
                              {!domain.is_verified && (
                                <Button
                                  variant="ghost" size="sm"
                                  onClick={() => handleMarkDomainActive(domain.id)}
                                  className="text-[#E9FF60] hover:text-white text-xs"
                                  data-testid={`mark-active-${domain.id}`}
                                >
                                  ✓ Marcar activo
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteDomain(domain.id)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Instrucciones de configuración */}
                          <div className="bg-[#111] px-4 py-4 border-t border-[#222]">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Instrucciones para el cliente</p>
                            
                            {/* Paso 1 */}
                            <div className="mb-3">
                              <p className="text-xs text-[#E9FF60] font-semibold mb-1">Paso 1 — Agregar dominio a Cloudflare</p>
                              <p className="text-xs text-gray-400">
                                El cliente debe agregar <span className="font-mono text-white">{domain.custom_domain}</span> a su cuenta de Cloudflare
                                (cambiar los nameservers en nic.ar a los de Cloudflare).
                              </p>
                            </div>

                            {/* Paso 2 */}
                            <div className="mb-3">
                              <p className="text-xs text-[#E9FF60] font-semibold mb-1">Paso 2 — Configurar DNS en Cloudflare</p>
                              <div className="font-mono text-xs bg-[#0a0a0a] rounded-lg p-2 border border-[#333]">
                                <table className="w-full">
                                  <thead><tr className="text-gray-600">
                                    <th className="text-left pb-1 pr-3">Tipo</th>
                                    <th className="text-left pb-1 pr-3">Nombre</th>
                                    <th className="text-left pb-1 pr-3">Valor</th>
                                    <th className="text-left pb-1">Proxy</th>
                                  </tr></thead>
                                  <tbody>
                                    <tr>
                                      <td className="pr-3 py-1"><span className="px-1 bg-orange-500/20 text-orange-400 rounded">CNAME</span></td>
                                      <td className="pr-3 py-1 text-gray-300">@</td>
                                      <td className="pr-3 py-1 text-[#E9FF60]">{mainDomainConfig?.domain || 'tu-dominio.com.ar'}</td>
                                      <td className="py-1 text-orange-400">☁ Proxy ON</td>
                                    </tr>
                                    <tr>
                                      <td className="pr-3 py-1"><span className="px-1 bg-orange-500/20 text-orange-400 rounded">CNAME</span></td>
                                      <td className="pr-3 py-1 text-gray-300">www</td>
                                      <td className="pr-3 py-1 text-[#E9FF60]">{mainDomainConfig?.domain || 'tu-dominio.com.ar'}</td>
                                      <td className="py-1 text-orange-400">☁ Proxy ON</td>
                                    </tr>
                                  </tbody>
                                </table>
                                <button
                                  onClick={() => {
                                    const txt = `CNAME @ → ${mainDomainConfig?.domain || 'tu-dominio.com.ar'} (Proxy ON)\nCNAME www → ${mainDomainConfig?.domain || 'tu-dominio.com.ar'} (Proxy ON)`;
                                    navigator.clipboard.writeText(txt);
                                    toast.success('DNS copiado');
                                  }}
                                  className="mt-2 text-xs text-gray-500 hover:text-white flex items-center gap-1"
                                >
                                  <Copy className="w-3 h-3" /> Copiar
                                </button>
                              </div>
                              <p className="text-xs text-gray-600 mt-1">
                                nic.ar no soporta CNAME en el root, pero Cloudflare lo aplana automáticamente. Con "Proxy ON" (nube naranja), Cloudflare maneja el SSL automáticamente.
                              </p>
                            </div>

                            {/* Paso 3 - Worker */}
                            <div className="mb-3">
                              <p className="text-xs text-[#E9FF60] font-semibold mb-1">Paso 3 — Crear Cloudflare Worker</p>
                              <p className="text-xs text-gray-400 mb-2">
                                En el panel de Cloudflare: <span className="text-white">Workers & Pages → Create Worker</span>.
                                Pegar este script, deployar, y luego agregar una ruta: <span className="font-mono text-white">{domain.custom_domain}/*</span>
                              </p>
                              <div className="relative">
                                <pre className="text-xs bg-[#0a0a0a] border border-[#333] rounded-lg p-3 overflow-x-auto text-gray-300 leading-relaxed">
{generateWorkerScript(mainDomainConfig?.domain)}
                                </pre>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(generateWorkerScript(mainDomainConfig?.domain));
                                    toast.success('Script del Worker copiado');
                                  }}
                                  className="absolute top-2 right-2 text-xs bg-[#333] hover:bg-[#444] text-gray-300 px-2 py-1 rounded flex items-center gap-1"
                                >
                                  <Copy className="w-3 h-3" /> Copiar
                                </button>
                              </div>
                            </div>

                            {/* Paso 4 - Marcar activo */}
                            <div>
                              <p className="text-xs text-[#E9FF60] font-semibold mb-1">Paso 4 — Confirmar activación</p>
                              <p className="text-xs text-gray-400">
                                Una vez que el Worker esté activo y el DNS propagado (puede tardar hasta 24hs en nic.ar),
                                hacé clic en <span className="text-white">"Verificar DNS"</span> o en <span className="text-white">"Marcar activo"</span> si ya confirmaste que funciona.
                              </p>
                            </div>

                            {domain.last_verified && (
                              <p className="text-xs text-gray-700 mt-3 pt-3 border-t border-[#222]">
                                Última verificación: {new Date(domain.last_verified).toLocaleString('es-AR')}
                                {domain.resolved_ips?.length > 0 && ` · IPs: ${domain.resolved_ips.join(', ')}`}
                                {domain.cname_targets?.length > 0 && ` · CNAME → ${domain.cname_targets.join(', ')}`}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Domain Modal */}
              <Dialog open={showDomainModal} onOpenChange={setShowDomainModal}>
                <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
                  <DialogHeader>
                    <DialogTitle style={{ color: '#E9FF60' }}>Agregar Dominio de Negocio</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Dominio Personalizado</label>
                      <Input
                        value={domainForm.custom_domain}
                        onChange={(e) => setDomainForm({ ...domainForm, custom_domain: e.target.value.toLowerCase().trim() })}
                        placeholder="mi-restaurante.com"
                        className="bg-[#0a0a0a] border-[#333] text-white font-mono"
                        data-testid="client-domain-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Sin http:// ni www. Ej: <span className="font-mono">burgerhouse.com</span></p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-gray-300">Asignar a Negocio</label>
                      <select
                        value={domainForm.business_id}
                        onChange={(e) => setDomainForm({ ...domainForm, business_id: e.target.value })}
                        className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                        data-testid="client-domain-business-select"
                      >
                        <option value="">Seleccionar negocio...</option>
                        {businesses.map((b) => (
                          <option key={b.id} value={b.id}>{b.name} (/{b.slug})</option>
                        ))}
                      </select>
                    </div>
                    {serverIP && (
                      <div className="p-3 bg-[#E9FF60]/5 border border-[#E9FF60]/20 rounded-lg">
                        <p className="text-xs text-gray-400">
                          El cliente deberá crear un registro DNS tipo <strong className="text-white">A</strong> apuntando a:
                        </p>
                        <p className="text-sm font-mono font-bold text-[#E9FF60] mt-1">{serverIP}</p>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowDomainModal(false)} className="border-[#333]">Cancelar</Button>
                    <Button onClick={handleAddDomain} className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black font-bold" data-testid="confirm-add-domain-btn">Agregar Dominio</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </main>
      </div>

      {/* MODALES - Crear, Editar, Eliminar */}
      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>Crear Nuevo Negocio</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateBusiness} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Nombre del Negocio *</label>
                <Input
                  value={businessForm.name}
                  onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                  required
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="new-business-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Slug (URL) *</label>
                <Input
                  value={businessForm.slug}
                  onChange={(e) => setBusinessForm({ ...businessForm, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  placeholder="ej: pizza-don-jose"
                  required
                  className="bg-[#0a0a0a] border-[#333] text-white"
                  data-testid="new-business-slug"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Email del Dueño *</label>
                <Input
                  type="email"
                  value={businessForm.owner_email}
                  onChange={(e) => setBusinessForm({ ...businessForm, owner_email: e.target.value })}
                  required
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Contraseña *</label>
                <Input
                  type="password"
                  value={businessForm.password}
                  onChange={(e) => setBusinessForm({ ...businessForm, password: e.target.value })}
                  required
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">WhatsApp *</label>
              <Input
                value={businessForm.whatsapp}
                onChange={(e) => setBusinessForm({ ...businessForm, whatsapp: e.target.value.replace(/\D/g, '') })}
                placeholder="54234512345"
                required
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Color Primario</label>
                <Input
                  type="text"
                  value={businessForm.primary_color}
                  onChange={(e) => setBusinessForm({ ...businessForm, primary_color: e.target.value })}
                  placeholder="#FF6B35"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color Acento</label>
                <Input
                  type="text"
                  value={businessForm.accent_color}
                  onChange={(e) => setBusinessForm({ ...businessForm, accent_color: e.target.value })}
                  placeholder="#E9FF60"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color Fondo</label>
                <Input
                  type="text"
                  value={businessForm.background_color}
                  onChange={(e) => setBusinessForm({ ...businessForm, background_color: e.target.value })}
                  placeholder="#0D0D0D"
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tipografía Títulos</label>
                <select
                  value={businessForm.font_heading}
                  onChange={(e) => setBusinessForm({ ...businessForm, font_heading: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                >
                  <option>Playfair Display</option>
                  <option>Montserrat</option>
                  <option>Lora</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tipografía Cuerpo</label>
                <select
                  value={businessForm.font_body}
                  onChange={(e) => setBusinessForm({ ...businessForm, font_body: e.target.value })}
                  className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white"
                >
                  <option>Roboto</option>
                  <option>Poppins</option>
                  <option>Open Sans</option>
                </select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="border-[#333]">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black">
                <Save className="w-4 h-4 mr-2" />
                Crear Negocio
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal - Similar structure */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>Editar Negocio — {selectedBusiness?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditBusiness} className="space-y-6">
            
            {/* Datos Básicos */}
            <div>
              <h4 className="text-sm font-bold text-gray-400 uppercase mb-3 tracking-wide">Datos Básicos</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nombre del Negocio *</label>
                  <Input
                    value={businessForm.name}
                    onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                    required
                    className="bg-[#0a0a0a] border-[#333] text-white"
                    data-testid="edit-business-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp</label>
                  <Input
                    value={businessForm.whatsapp}
                    onChange={(e) => setBusinessForm({ ...businessForm, whatsapp: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email del propietario</label>
                  <Input
                    type="email"
                    value={businessForm.owner_email}
                    onChange={(e) => setBusinessForm({ ...businessForm, owner_email: e.target.value })}
                    className="bg-[#0a0a0a] border-[#333] text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Nueva Contraseña <span className="text-gray-500 text-xs">(dejar vacío para no cambiar)</span></label>
                  <Input
                    type="password"
                    value={businessForm.password}
                    onChange={(e) => setBusinessForm({ ...businessForm, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-[#0a0a0a] border-[#333] text-white"
                    data-testid="edit-business-password"
                  />
                </div>
              </div>
            </div>

            {/* Colores */}
            <div>
              <h4 className="text-sm font-bold text-gray-400 uppercase mb-3 tracking-wide">Diseño</h4>
              <div className="grid grid-cols-5 gap-3">
                {[
                  { key: 'primary_color', label: 'Primario' },
                  { key: 'accent_color', label: 'Acento' },
                  { key: 'background_color', label: 'Fondo' },
                  { key: 'text_color', label: 'Texto' },
                  { key: 'card_color', label: 'Tarjeta' },
                ].map((c) => (
                  <div key={c.key}>
                    <label className="block text-xs font-medium mb-1 text-gray-400">{c.label}</label>
                    <div className="flex gap-1 items-center">
                      <input type="color" value={businessForm[c.key] || '#000000'} onChange={(e) => setBusinessForm({ ...businessForm, [c.key]: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                      <Input type="text" value={businessForm[c.key] || ''} onChange={(e) => setBusinessForm({ ...businessForm, [c.key]: e.target.value })} className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-xs p-1" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-400">Fuente Títulos</label>
                  <select value={businessForm.font_heading} onChange={(e) => setBusinessForm({ ...businessForm, font_heading: e.target.value })} className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white text-sm">
                    {(fonts.length > 0 ? fonts : [
                      { name: 'Playfair Display' }, { name: 'Montserrat' }, { name: 'Lora' }, { name: 'Poppins' }
                    ]).map(f => (
                      <option key={f.id || f.name} value={f.name}>{f.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1 text-gray-400">Fuente Cuerpo</label>
                  <select value={businessForm.font_body} onChange={(e) => setBusinessForm({ ...businessForm, font_body: e.target.value })} className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#333] rounded-md text-white text-sm">
                    {(fonts.length > 0 ? fonts : [
                      { name: 'Roboto' }, { name: 'Poppins' }, { name: 'Open Sans' }
                    ]).map(f => (
                      <option key={f.id || f.name} value={f.name}>{f.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Panel Config - Enable/Disable sections */}
            <div>
              <h4 className="text-sm font-bold text-gray-400 uppercase mb-3 tracking-wide">Secciones del Panel Admin</h4>
              <p className="text-xs text-gray-500 mb-3">Activá o desactivá qué secciones puede ver el administrador del negocio</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'orders', label: 'Pedidos' },
                  { key: 'customers', label: 'Clientes' },
                  { key: 'branding', label: 'Branding y Diseño' },
                  { key: 'categories', label: 'Categorías' },
                  { key: 'products', label: 'Productos' },
                  { key: 'hours', label: 'Horarios' },
                  { key: 'zones', label: 'Zonas de Envío' },
                  { key: 'payments', label: 'Pagos' },
                ].map((section) => (
                  <label key={section.key} className="flex items-center gap-3 p-3 bg-[#0a0a0a] rounded-lg cursor-pointer hover:bg-[#111] transition-colors">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={businessForm.panel_config?.[section.key] !== false}
                        onChange={(e) => setBusinessForm({
                          ...businessForm,
                          panel_config: {
                            ...(businessForm.panel_config || {}),
                            [section.key]: e.target.checked
                          }
                        })}
                        className="sr-only"
                        data-testid={`panel-config-${section.key}`}
                      />
                      <div className={`w-10 h-5 rounded-full transition-colors ${
                        businessForm.panel_config?.[section.key] !== false ? 'bg-[#E9FF60]' : 'bg-[#333]'
                      }`}>
                        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform ${
                          businessForm.panel_config?.[section.key] !== false ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </div>
                    </div>
                    <span className="text-sm text-gray-300">{section.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="border-[#333]">
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black" data-testid="save-edit-business">
                <Save className="w-4 h-4 mr-2" />
                Guardar Cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-6 h-6" />
              Confirmar Eliminación
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-300 mb-4">
              ¿Estás segura de que deseas eliminar <strong style={{ color: '#E9FF60' }}>{selectedBusiness?.name}</strong>?
            </p>
            <p className="text-sm text-gray-500">
              Esta acción eliminará permanentemente:
            </p>
            <ul className="text-sm text-gray-500 list-disc list-inside mt-2">
              <li>El negocio y su configuración</li>
              <li>Todas las categorías</li>
              <li>Todos los productos</li>
            </ul>
            <p className="text-sm text-red-500 mt-4 font-semibold">
              ⚠️ Esta acción no se puede deshacer
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="border-[#333]">
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteBusiness} 
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Font Upload Modal */}
      <Dialog open={showFontModal} onOpenChange={setShowFontModal}>
        <DialogContent className="max-w-md bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>Agregar Nueva Fuente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Archivo de Fuente *</label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept=".otf,.ttf,.woff,.woff2"
                  onChange={handleFontUpload}
                  className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#E9FF60] file:text-black hover:file:bg-[#f0ff80] cursor-pointer"
                />
                <p className="text-xs text-gray-500">
                  Formatos permitidos: <code className="text-[#E9FF60]">.otf</code>, <code className="text-[#E9FF60]">.ttf</code>, <code className="text-[#E9FF60]">.woff</code>, <code className="text-[#E9FF60]">.woff2</code>
                </p>
              </div>
            </div>

            {fontFile && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">Nombre de la Fuente *</label>
                  <Input
                    value={fontName}
                    onChange={(e) => setFontName(e.target.value)}
                    placeholder="Ej: Mi Fuente Custom"
                    className="bg-[#0a0a0a] border-[#333] text-white"
                  />
                </div>

                <div className="p-3 bg-[#0a0a0a] rounded">
                  <div className="text-xs text-gray-400 mb-1">Archivo seleccionado:</div>
                  <div className="text-sm font-semibold" style={{ color: '#E9FF60' }}>
                    📄 {fontFile.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Tamaño: {(fontFile.size / 1024).toFixed(2)} KB
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowFontModal(false);
                setFontFile(null);
                setFontName('');
              }} 
              className="border-[#333]"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveFont}
              disabled={!fontFile || !fontName}
              className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black"
            >
              <Save className="w-4 h-4 mr-2" />
              Guardar Fuente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preset Creation Modal */}
      <Dialog open={showPresetModal} onOpenChange={setShowPresetModal}>
        <DialogContent className="max-w-lg bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle style={{ color: '#E9FF60' }}>Crear Nuevo Preset de Colores</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nombre del Preset *</label>
              <Input
                value={presetForm.name}
                onChange={(e) => setPresetForm({ ...presetForm, name: e.target.value })}
                placeholder="Ej: Elegante Oscuro"
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Color Primario</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={presetForm.primary_color}
                    onChange={(e) => setPresetForm({ ...presetForm, primary_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={presetForm.primary_color}
                    onChange={(e) => setPresetForm({ ...presetForm, primary_color: e.target.value })}
                    className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color Acento</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={presetForm.accent_color}
                    onChange={(e) => setPresetForm({ ...presetForm, accent_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={presetForm.accent_color}
                    onChange={(e) => setPresetForm({ ...presetForm, accent_color: e.target.value })}
                    className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color Fondo</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={presetForm.background_color}
                    onChange={(e) => setPresetForm({ ...presetForm, background_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={presetForm.background_color}
                    onChange={(e) => setPresetForm({ ...presetForm, background_color: e.target.value })}
                    className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color Texto</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={presetForm.text_color}
                    onChange={(e) => setPresetForm({ ...presetForm, text_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={presetForm.text_color}
                    onChange={(e) => setPresetForm({ ...presetForm, text_color: e.target.value })}
                    className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color Tarjetas</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={presetForm.card_color}
                    onChange={(e) => setPresetForm({ ...presetForm, card_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={presetForm.card_color}
                    onChange={(e) => setPresetForm({ ...presetForm, card_color: e.target.value })}
                    className="flex-1 bg-[#0a0a0a] border-[#333] text-white font-mono text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg" style={{ backgroundColor: presetForm.background_color }}>
              <div className="text-sm font-semibold mb-2" style={{ color: presetForm.text_color }}>
                Vista Previa
              </div>
              <div className="flex gap-2">
                <div 
                  className="w-16 h-16 rounded flex items-center justify-center text-xs font-bold"
                  style={{ 
                    backgroundColor: presetForm.primary_color,
                    color: presetForm.background_color
                  }}
                >
                  Primario
                </div>
                <div 
                  className="w-16 h-16 rounded flex items-center justify-center text-xs font-bold"
                  style={{ 
                    backgroundColor: presetForm.accent_color,
                    color: presetForm.background_color
                  }}
                >
                  Acento
                </div>
                <div 
                  className="w-16 h-16 rounded flex items-center justify-center text-xs"
                  style={{ 
                    backgroundColor: presetForm.card_color,
                    color: presetForm.text_color
                  }}
                >
                  Tarjeta
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowPresetModal(false)} 
              className="border-[#333]"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSavePreset}
              disabled={!presetForm.name}
              className="bg-[#E9FF60] hover:bg-[#f0ff80] text-black"
            >
              <Save className="w-4 h-4 mr-2" />
              Crear Preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminDashboard;
